import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { AvatarGroup, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { useIsFetching } from '@tanstack/react-query';
import startCase from 'lodash/startCase';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import { Fragment } from 'react';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify, TextMaxLine } from '@mgm/common/components';
import {
  MemoizedBaseAvatar, BaseTooltip, BaseLabel, BaseImage,
} from '@mgm/common/components/base';
import { getCommissionPlans } from '../../../store/commissions/selectors';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../store/common/selectors';
import { CommissionPlanPopover } from './CommissionPlanPopover';
import { getMembers } from '../../../store/team/selectors';
import { CommissionPlanSkeleton } from './CommissionPlanSkeleton';
import { QUERY_KEYS } from '../../../utils/enums';
import { EmptyCommissionPlans } from './EmptyCommissionPlans';

const CommissionPlanContent = () => {
  const { t } = useTranslation(['tasklists', 'commissions']);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const members = useAppSelector(getMembers);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const vertical = false;

  const isFetching = useIsFetching({
    queryKey: [QUERY_KEYS.COMMISSIONS.GET_PLANS],
  });

  const getMembersByCommissionId = (planId: number) => members.filter((member) => member.commissionPlan?.id === planId) || [];

  if (isFetching === 1) {
    return <CommissionPlanSkeleton />;
  }

  if (commissionPlans.length === 0) {
    return <EmptyCommissionPlans />;
  }

  return (
    <Grid width="100%" container spacing={2}>
      {commissionPlans.map((plan) => {
        const planMembers = getMembersByCommissionId(plan.id);

        // eslint-disable-next-line no-nested-ternary
        const planIcon = plan.type === 'Products' ? 'fluent-mdl2:product-variant' : plan.type === 'Fixed' ? 'mdi:lock-rate-outline' : 'lucide-lab:stairs-arrow-up-right';

        // eslint-disable-next-line no-nested-ternary
        const planColor = plan.type === 'Products' ? '#4a599f' : plan.type === 'Fixed' ? '#A855F7' : '#005249';

        return (
          <Grid
            size={{
              xs: 12,
              md: 6,
              sm: 12,
              lg: 4,
            }}
            key={plan.id}
          >
            <Card
              sx={{
                display: { sm: 'flex' },
                '&:hover': {
                  boxShadow: (theme) => (theme as TCustomTheme).customShadows.z24,
                },
              }}
            >

              <Stack width="100%" spacing={1.5} sx={{ p: 3 }}>
                <Stack
                  spacing={{
                    xs: 3,
                    sm: vertical ? 3 : 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <div id="commission-plan-type">
                      <Box display="flex" alignItems="center">
                        <Typography pr={2} variant="overline" sx={{ color: planColor }}>
                          {t(`${plan.type?.toLowerCase()}`, { ns: 'commissions' })}
                        </Typography>
                        <Iconify
                          icon={planIcon}
                          width={20}
                          sx={{
                            color: planColor,
                          }}
                        />
                      </Box>
                    </div>

                    <CommissionPlanPopover plan={plan} />
                  </Stack>

                  <Stack spacing={1}>
                    <TextMaxLine variant="subtitle1" line={1}>
                      {plan.name}
                    </TextMaxLine>

                    <TextMaxLine
                      variant="caption"
                      color="text.secondary"
                    >
                      {plan.description}
                    </TextMaxLine>
                  </Stack>
                </Stack>

                <Stack
                  spacing={1.5}
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  divider={<Divider orientation="vertical" sx={{ height: 20, my: 'auto', borderStyle: 'dashed' }} />}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <BaseTooltip tooltipText={`${t('createdBy', { ns: 'commissions' })} ${plan.creator.name}`}>
                      <MemoizedBaseAvatar
                        avatarFile={plan.creator.avatarFile || ''}
                        avatarName={plan.creator.name}
                      />
                    </BaseTooltip>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('createdAt', { ns: 'commissions' })}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ typography: 'subtitle2' }}>
                    <Box component="span" typography="body2" sx={{ ml: 0.5 }}>
                      {moment(plan.createdAt).format('MMM Do YYYY')}
                    </Box>
                  </Stack>
                </Stack>

                <Divider sx={{ borderStyle: 'dashed' }} />

                {planMembers?.length > 0 && (
                <Stack
                  justifyContent="space-between"
                  alignItems="center"
                  direction="row"
                  id="commission-plan-employees"
                  spacing={3}
                >
                  <BaseLabel
                    color={plan.type === 'Fixed' ? 'primary' : 'success'}
                    letterSpacing="-0.5px"
                  >
                    {`${t('employees', { ns: 'commissions' })}`}
                  </BaseLabel>
                  {planMembers && (
                  <Tooltip
                    title={(
                      <Stack p={2} direction="column" spacing={2}>
                        {planMembers.map((member) => (
                          <Stack
                            key={member.email}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <MemoizedBaseAvatar
                              sx={{
                                width: 37,
                                height: 37,
                              }}
                              avatarName={member.name}
                              avatarFile={member.avatarFile}
                            />
                            <Typography>{startCase(member.name)}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      )}
                    placement="top"
                    arrow
                  >
                    <AvatarGroup
                      total={planMembers.length}
                      max={3}
                      sx={{
                        '.MuiAvatarGroup-avatar:first-of-type': {
                          width: 30,
                          height: 30,
                        },
                      }}
                    >
                      {planMembers.map((member) => (
                        <MemoizedBaseAvatar
                          key={member.email}
                          sx={{
                            marginRight: '0',
                          }}
                          size={{
                            width: 25,
                            height: 25,
                          }}
                          avatarName={member.name}
                          avatarFile={member.avatarFile}
                        />
                      ))}
                    </AvatarGroup>
                  </Tooltip>
                  )}
                </Stack>
                )}

                <Stack justifyContent="space-between" alignItems="center" direction="row" id="commission-plan-commission" spacing={1}>
                  <BaseLabel
                    color={plan.type === 'Fixed' ? 'primary' : 'success'}
                    letterSpacing="-0.5px"
                  >
                    {`${t('commission', { ns: 'commissions' })}`}
                  </BaseLabel>
                  {plan.type === 'Products' && (
                    <BaseTooltip
                      tooltipText={(
                        <Stack spacing={1}>
                          {plan.productCommissions?.map((item, idx) => (
                            <Stack alignItems="center" key={idx?.toString()} p={1} direction="row">
                              <BaseImage
                                alt={item?.title || 'Product'}
                                src={item.imageUrl}
                                sx={{ borderRadius: 1.5, width: 40, height: 40 }}
                              />
                              <Typography px={1} variant="body2">
                                {item.displayName}
                                {' '}
                                ---
                                {' '}
                                {' '}
                                {item.fixedType === 'flat'
                                  ? `${getCurrency().mark}${item.fixedAmount}`
                                  : `${item.fixedAmount}%`}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="overline">
                          {plan.productCommissions?.length}
                          {' '}
                          {t('products', { ns: 'commissions' })}
                        </Typography>
                      </Stack>
                    </BaseTooltip>
                  )}
                  {plan.type === 'Fixed' ? (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="overline">
                        {plan.fixedCommission.fixedAmount}
                        {plan.fixedCommission.fixedType === 'flat' ? getCurrency().mark : '%'}
                        {' '}
                      </Typography>
                      <Divider sx={{ borderStyle: 'dashed' }} orientation="vertical" variant="middle" flexItem />
                      <Stack direction="column" alignItems="center">
                        <Typography variant="overline">
                          {t('perSale', { ns: 'commissions' })}
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : (
                    plan.type === 'Steps' && (
                      <Stack direction="column" spacing={1}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Stack direction="column" alignItems="center">
                            <Typography variant="overline">
                              {Number(plan.commissionSteps[0].percent) > 0 ? '%' : getCurrency().mark}
                              {plan.commissionSteps[0].amount || plan.commissionSteps[0].percent}
                            </Typography>
                          </Stack>
                          <Stack direction="column" alignItems="center">
                            <Typography alignItems="center" display="flex" variant="caption">
                              {isMobile ? '-' : '---'}
                            </Typography>
                          </Stack>
                          <Stack direction="column" alignItems="center">
                            <Typography variant="overline">
                              {/* eslint-disable-next-line no-unsafe-optional-chaining */}
                              {Number(plan.commissionSteps[Number(plan?.commissionSteps?.length - 1)].percent) > 0
                                ? '%' : getCurrency().mark}
                              {/* eslint-disable-next-line no-unsafe-optional-chaining */}
                              {plan.commissionSteps[Number(plan?.commissionSteps?.length - 1)]?.amount
                                // eslint-disable-next-line no-unsafe-optional-chaining
                                || plan.commissionSteps[Number(plan?.commissionSteps?.length - 1)]?.percent}
                            </Typography>
                          </Stack>
                          <Divider sx={{ borderStyle: 'dashed' }} orientation="vertical" variant="middle" flexItem />
                          <Stack direction="column" alignItems="center">
                            <Typography variant="overline">
                              {t('perSale', { ns: 'commissions' })}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    )
                  )}
                </Stack>

                {plan?.bonuses?.length > 0 && Number(plan.bonuses?.[0]?.fixedAmount) > 0 && (
                  <Stack justifyContent="space-between" alignItems="center" direction="row" spacing={1}>
                    <BaseLabel
                      color={plan.type === 'Fixed' ? 'primary' : 'success'}
                      letterSpacing="-0.5px"
                    >
                      {`${t('bonuses')}`}
                    </BaseLabel>
                    <Stack direction="row" spacing={1}>
                      {plan.bonuses?.map((bonus, index) => (
                        <Fragment key={bonus.index}>
                          <Stack direction="row" spacing={1}>
                            <Typography pt={0.1} variant="overline">
                              {bonus.name}
                              {' '}
                              ---
                            </Typography>
                            <Typography variant="caption">
                              {bonus.fixedAmount}
                              {bonus.fixedType === 'flat' ? getCurrency().mark : '%'}
                            </Typography>
                          </Stack>
                          {index > 0 && <Divider sx={{ borderStyle: 'dashed' }} orientation="vertical" variant="middle" flexItem />}
                        </Fragment>
                      ))}
                    </Stack>
                  </Stack>
                )}

                <Divider
                  sx={{
                    borderStyle: 'dashed',
                    display: { sm: 'none' },
                  }}
                />

                {plan.updatedAt && (
                  <Stack
                    spacing={1}
                    justifyContent="space-between"
                    direction="row"
                    alignItems="center"
                    sx={{ typography: 'body2' }}
                  >
                    <BaseLabel
                      color={plan.type === 'Fixed' ? 'primary' : 'success'}
                      letterSpacing="-0.5px"
                    >
                      {t('updatedAt', { ns: 'commissions' })}
                    </BaseLabel>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption">
                        {`${moment(plan.updatedAt).format('MMM Do YYYY')} --`}
                      </Typography>
                      <BaseTooltip tooltipText={`${t('updatedBy', { ns: 'commissions' })} ${plan.updatedBy.name}`}>
                        <MemoizedBaseAvatar
                          key={plan.updatedBy.email}
                          sx={{
                            marginRight: '0',
                          }}
                          size={{
                            width: 25,
                            height: 25,
                          }}
                          avatarName={plan.updatedBy?.name}
                          avatarFile={plan.updatedBy.avatarFile || null}
                        />
                      </BaseTooltip>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export {
  CommissionPlanContent,
};
